/* eslint-disable import/no-duplicates */
import type React from 'react';
import { useEffect, useState } from 'react';
import { type LoaderFunctionArgs } from 'react-router';
import { z } from 'zod';
import { zx } from 'zodix';

import { BackButton } from '~/components/back-button';
import { EventPurchaseContent } from '~/components/events/event-purchase-content';
import { EventWaiverDescription } from '~/components/events/event-waiver-description';
import { HouseholdMemberSelectionModal } from '~/components/modals/household-member-selection-modal';
import { WaiverModal } from '~/components/modals/waiver-modal';
import { WaiverPreviewModal } from '~/components/modals/waiver-preview-modal';
import BackgroundThumbnailWithFallback from '~/components/photos/background-thumbnail-with-fallback';
import { RichTextHtmlViewer } from '~/components/rich-text/rich-text-html-viewer';
import { SharePageButton } from '~/components/share-page-button';
import { Alert } from '~/components/ui/alert';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Icon } from '~/components/ui/icon';
import { StatusButton } from '~/components/ui/status-button';
import { getEventDetails } from '~/domain/events/handlers/get-event-details.server';
import { getEventFee } from '~/domain/fees/queries/get-event-fee.server';
import { getHouseholdMembersForEventRegistration } from '~/domain/households/queries/get-household-members-for-event-registration.server';
import { SCREENS } from '~/styles/extended-theme';
import { getSessionIfAuthenticated } from '~/utils/auth.server';
import { fromStripeFormat, getTotalWithPercentageFee } from '~/utils/currency';
import { handleDomainError } from '~/utils/errors.server';
import { formatEventDate } from '~/utils/format-event-times';
import { useIsBelowBreakpoint } from '~/utils/use-is-below-breakpoint';

import {
  DefaultPromoCodeHelperContent,
  PurchaseModal,
} from '../gym+/$slug+/components/purchase-modal';

import { ButtonAction, useEventTicketing } from './$eventId.tickets';
import { type Route } from './+types/$eventId';
import { AuthRequired } from './components/auth-required';
import { EventAddress } from './components/event-address';
import { EventCard } from './components/event-card';
import { PresenterInformation } from './components/presenter-information';
import { Section } from './components/section';

export async function loader({ request, params }: LoaderFunctionArgs) {
  const session = await getSessionIfAuthenticated(request);
  const { eventId } = zx.parseParams(params, {
    eventId: z.coerce.number(),
  });

  const result = await getEventDetails(session, eventId);
  const feePercentage = await getEventFee();

  let householdData = null;
  if (session) {
    householdData = await getHouseholdMembersForEventRegistration(session.userId);
  }

  return result
    .map((eventDetails) => ({
      eventDetails,
      eventPrice:
        !eventDetails.freeEvent && eventDetails.eventPriceCents
          ? getTotalWithPercentageFee(eventDetails.eventPriceCents, feePercentage)
          : null,
      householdData,
    }))
    .mapErr(handleDomainError)
    .unwrap();
}

const renderButtonAction = (
  buttonAction: ButtonAction,
  eventPrice: number | null,
): React.ReactNode => {
  switch (buttonAction) {
    case ButtonAction.BuyTicket:
      return eventPrice != null ? `Register - ${fromStripeFormat(eventPrice)}` : 'Register';
    case ButtonAction.GetFreeTicket:
      return 'Register';
    case ButtonAction.DeleteFreeTicket:
      return (
        <>
          Going&nbsp;
          <Icon name="check" />
        </>
      );
  }
};

type LoaderData = Awaited<ReturnType<typeof loader>>;

function TicketingButton({
  eventDetails,
  eventPrice,
  householdData,
  onHouseholdMembersDeletedChange,
}: {
  eventDetails: LoaderData['eventDetails'];
  eventPrice: number | null;
  householdData: LoaderData['householdData'];
  onHouseholdMembersDeletedChange?: (deleted: boolean) => void;
}) {
  const {
    showingPaymentModal,
    showingWaiverModal,
    showingHouseholdModal,
    selectedMemberIds,
    stripeClientSecret,
    promoCode,
    amount,
    amountBelowMinimum,
    promoCodeInvalid,
    paymentId,
    isSubmitting,
    buttonAction,
    householdMembersDeleted,
    handleTicketAction,
    handlePaymentModalClose,
    handleWaiverModalClose,
    handleWaiverAccept,
    handleCreateEventPaymentPreCheck,
    handlePromoCodeSubmit,
    handleHouseholdModalClose,
    handleHouseholdModalContinue,
  } = useEventTicketing({
    event: eventDetails,
    gymSlugOrId: eventDetails.gym.id,
    householdData,
    eventPrice,
  });

  // Notify parent when household members deleted state changes
  useEffect(() => {
    if (onHouseholdMembersDeletedChange) {
      onHouseholdMembersDeletedChange(householdMembersDeleted);
    }
  }, [householdMembersDeleted, onHouseholdMembersDeletedChange]);

  const dateString = formatEventDate(eventDetails.date);

  const shouldDisableButton = () => {
    if (isSubmitting) {
      return true;
    }

    if (buttonAction === ButtonAction.DeleteFreeTicket) {
      return false;
    }

    return !eventDetails.hasAvailableTicketCapacity;
  };

  const registrationCount = selectedMemberIds.length > 0 ? selectedMemberIds.length : 1;

  return (
    <>
      <StatusButton
        className="h-auto min-h-9 flex-1"
        onClick={() => handleTicketAction()}
        disabled={shouldDisableButton()}
        type="submit"
        variant={buttonAction === ButtonAction.DeleteFreeTicket ? 'outline' : 'default'}
        color="primary"
        status={isSubmitting ? 'pending' : 'idle'}
      >
        {renderButtonAction(buttonAction, eventPrice)}
      </StatusButton>

      {showingWaiverModal ? (
        <AuthRequired>
          <WaiverModal
            open={showingWaiverModal}
            waiverHtml={eventDetails.waiverHtml!}
            onClose={handleWaiverModalClose}
            onAccept={handleWaiverAccept}
            householdMembers={householdData?.members}
            selectedMemberIds={selectedMemberIds}
            authenticatedMemberId={householdData?.authenticatedMemberId ?? null}
            description={
              <EventWaiverDescription
                gymName={eventDetails.gym.name}
                name={eventDetails.name}
                freeEvent={eventDetails.freeEvent}
                startTime={eventDetails.startTime}
                endTime={eventDetails.endTime}
                date={dateString}
              />
            }
          />
        </AuthRequired>
      ) : null}
      {showingHouseholdModal && householdData ? (
        <HouseholdMemberSelectionModal
          open={showingHouseholdModal}
          members={householdData.members}
          authenticatedAthleteId={householdData.authenticatedAthleteId}
          onClose={handleHouseholdModalClose}
          onContinue={handleHouseholdModalContinue}
        />
      ) : null}
      {showingPaymentModal && (stripeClientSecret !== null || paymentId !== undefined) ? (
        <PurchaseModal
          open
          onClose={handlePaymentModalClose}
          stripeClientSecret={stripeClientSecret}
          returnUrl={`${window.location.protocol}//${window.location.host}/events/${eventDetails.id}/tickets/return?path=/events/${eventDetails.id}`}
          title="Purchase Registration(s)"
          className="pt-0"
          onPrePaymentCheck={handleCreateEventPaymentPreCheck}
          onPromoCodeSubmit={handlePromoCodeSubmit}
          promoCode={promoCode}
          promoCodeInvalid={promoCodeInvalid}
          isLoading={isSubmitting}
          paymentId={paymentId}
          registrationCount={registrationCount}
          helperContent={
            <DefaultPromoCodeHelperContent amount={amount} amountBelowMinimum={amountBelowMinimum} />
          }
          content={
            <EventPurchaseContent
              gymName={eventDetails.gym.name}
              name={eventDetails.name}
              date={dateString}
              startTime={eventDetails.startTime}
              endTime={eventDetails.endTime}
              price={
                eventDetails.eventPriceCents
                  ? fromStripeFormat(
                      amount ??
                        (eventPrice !== null
                          ? eventPrice * registrationCount
                          : eventDetails.eventPriceCents),
                    )
                  : null
              }
              registrationCount={registrationCount}
            />
          }
        />
      ) : null}
    </>
  );
}

export default function EventDetails({
  loaderData: { eventDetails, eventPrice, householdData },
}: Route.ComponentProps) {
  const belowTablet = useIsBelowBreakpoint(SCREENS.Tablet);
  const [openWaiverModal, setOpenWaiverModal] = useState(false);
  const [householdMembersDeleted, setHouseholdMembersDeleted] = useState(false);

  const { presenter, ...event } = eventDetails;

  return (
    <>
      <div className="relative size-full">
        <BackButton defaultPath="/events" />
        <div
          className="relative mx-auto mt-[-20px] h-[238px] w-full overflow-hidden rounded-b-[16px] bg-gray-200 desktop:w-[1024px]"
          title="event background photo"
        >
          <BackgroundThumbnailWithFallback
            url={event.backgroundPhotoUrl?.url ?? '/img/fp-placeholder-header.png'}
            fallbackUrl={event.backgroundPhotoUrl?.fallbackUrl ?? '/img/fp-placeholder-header.png'}
            alt="event background"
            maxRetries={0}
            initialBackoff={0}
            rounded={false}
            showSpinnerWhileLoading
            spinnerColor="primary"
            className="size-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto my-4 mt-[-55px] px-4 pb-12 desktop:max-w-screen-lg">
          <div className="flex w-full justify-center">
            <div className="mb-6 flex flex-shrink flex-grow-0 basis-[500px] flex-col items-center gap-11">
              <EventCard className="w-full" event={eventDetails} includeGymLink={true} />
              {householdMembersDeleted ? (
                <Alert
                  title="Household Registration Canceled"
                  description="Registration for everyone in your household has been canceled. All household members' registrations have been removed from this event."
                  className="w-full"
                  titleClassName="text-subtitle2 text-foreground-danger"
                  descriptionClassName="text-body2 text-foreground-danger"
                />
              ) : null}
              <div className="flex w-full flex-row gap-3">
                <TicketingButton
                  eventDetails={eventDetails}
                  eventPrice={eventPrice}
                  householdData={householdData}
                  onHouseholdMembersDeletedChange={setHouseholdMembersDeleted}
                />
                <SharePageButton
                  variant="outline"
                  className="h-auto min-h-9 flex-1"
                  shareData={{ text: 'Check out this event in Fighters Passport:' }}
                >
                  Share
                </SharePageButton>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col justify-center gap-0 border-t tablet:flex-row tablet:gap-[30px]">
            <div className={presenter ? 'flex-1' : 'flex-shrink-1 flex-grow-0 basis-[500px]'}>
              {event.descriptionHtml ? (
                <Section title="Description">
                  <RichTextHtmlViewer rawHtml={event.descriptionHtml} className="!p-0" />
                </Section>
              ) : null}
              {event.equipmentRequirements.length > 0 ? (
                <Section title="Equipment Requirements" className="flex gap-2">
                  {event.equipmentRequirements.map((e) => (
                    <Badge key={e.id} text={e.name} />
                  ))}
                </Section>
              ) : null}
              <Section title="Location" className="flex flex-row gap-1">
                <Icon name="sewing-pin-filled" className="mt-1 self-start" />
                <EventAddress location={event.location} />
              </Section>
              {event.waiverHtml ? (
                <Section title="Waiver Required">
                  <p className="mb-2 text-body2">
                    You’ll be asked to sign our event waiver when you register.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpenWaiverModal(true);
                    }}
                  >
                    Review Waiver
                  </Button>
                  <WaiverPreviewModal
                    open={openWaiverModal}
                    onClose={() => {
                      setOpenWaiverModal(false);
                    }}
                    description="You’ll be asked to sign our event waiver when you register."
                    waiverHtml={event.waiverHtml}
                  />
                </Section>
              ) : null}
            </div>

            <PresenterInformation
              className={belowTablet ? 'border-t' : undefined}
              presenter={presenter}
            />
          </div>
        </div>
      </div>
    </>
  );
}
